import { lazy } from 'react';
import { RouteObject } from 'react-router-dom';
import { paths } from '@/shared/config/paths';
import { ADMIN_MANAGE_GRADES } from '@/shared/config/adminGrade';
import { withSuspense } from '../utils';
import ContestAdditionalRecruitment from '@/pages/contest/contest/ContestAdditionalRecruitment';

// 메인섹션
const TopBannerCreate = lazy(() => import('@/pages/mainSection/topBanner/TopBannerCreate'));
const TopBannerEdit = lazy(() => import('@/pages/mainSection/topBanner/TopBannerEdit'));
const PopupCreate = lazy(() => import('@/pages/mainSection/popup/PopupCreate'));
const PopupEdit = lazy(() => import('@/pages/mainSection/popup/PopupEdit'));
// 회원
const UserSendEmail = lazy(() => import('@/pages/user/email/UserSendEmail'));
const UserSendKakao = lazy(() => import('@/pages/user/kakao/UserSendKakao'));
// 대회
const ContestCreate = lazy(() => import('@pages/contest/contest/ContestCreate'));
const ContestUpdate = lazy(() => import('@pages/contest/contest/ContestEdit'));
const ContestSendEmail = lazy(() => import('@pages/contest/email/ContestSendEmail'));
const ContestSendKakao = lazy(() => import('@pages/contest/kakao/ContestSendKakao'));
// 프로그램
const ProgramCreate = lazy(() => import('@pages/program/program/ProgramCreate'));
const ProgramEdit = lazy(() => import('@pages/program/program/ProgramEdit'));
const SessionCreate = lazy(() => import('@pages/program/session/SessionCreate'));
const SessionEdit = lazy(() => import('@pages/program/session/SessionEdit'));
const ProgramSendEmail = lazy(() => import('@pages/program/email/ProgramSendEmail'));
const ProgramSendKakao = lazy(() => import('@pages/program/kakao/ProgramSendKakao'));
// 상품 관리
const ProductCreate = lazy(() => import('@pages/store/product/ProductCreate'));
const ProductEdit = lazy(() => import('@pages/store/product/ProductEdit'));
// 운영 관리
const NewsCreate = lazy(() => import('@pages/operation/news/NewsCreate'));
const NewsEdit = lazy(() => import('@pages/operation/news/NewsEdit'));
const NoticeCreate = lazy(() => import('@/pages/operation/notices/NoticeCreate'));
const NoticeEdit = lazy(() => import('@/pages/operation/notices/NoticeEdit'));
const FaqCreate = lazy(() => import('@/pages/operation/faq/FaqCreate'));
const FaqEdit = lazy(() => import('@/pages/operation/faq/FaqEdit'));
const InstagramCreate = lazy(() => import('@pages/operation/instagram/InstagramCreate'));
const InstagramEdit = lazy(() => import('@pages/operation/instagram/InstagramEdit'));
const YoutubeCreate = lazy(() => import('@pages/operation/youtube/YoutubeCreate'));
const YoutubeEdit = lazy(() => import('@pages/operation/youtube/YoutubeEdit'));
const PrivacyPolicyCreate = lazy(() => import('@pages/operation/privacyPolicy/PrivacyPolicyCreate'));
const TermsOfServiceCreate = lazy(() => import('@pages/operation/termsOfService/TermsOfServiceCreate'));
// 관리자
const AdminCreate = lazy(() => import('@pages/system/admin/AdminCreate'));
const AdminEdit = lazy(() => import('@pages/system/admin/AdminEdit'));

// 전체 화면 페이지
export const fullScreenRoutes: RouteObject[] = [
	// 메인섹션
	{
		// 최상단 배너
		path: paths.mainSection.topBanner.new.path,
		element: withSuspense(TopBannerCreate),
		handle: {
			title: '메인 최상단 배너 대회 등록',
			subTitle: '락앤런 대회·프로그램 중 메인 배너 등록',
			permission: 'MAIN_SECTION' as const
		}
	},
	{
		path: paths.mainSection.topBanner.edit.path,
		element: withSuspense(TopBannerEdit),
		handle: {
			title: '메인 최상단 배너 대회 수정',
			subTitle: '락앤런 대회·프로그램 중 메인 배너 수정',
			permission: 'MAIN_SECTION' as const
		}
	},
	{
		// 메인 팝업
		path: paths.mainSection.popup.new.path,
		element: withSuspense(PopupCreate),
		handle: {
			title: '메인 팝업 등록',
			subTitle: '락앤런 메인 팝업 등록하기',
			permission: 'MAIN_SECTION' as const
		}
	},
	{
		path: paths.mainSection.popup.edit.path,
		element: withSuspense(PopupEdit),
		handle: {
			title: '메인 팝업 수정',
			subTitle: '락앤런 메인 팝업 수정하기',
			permission: 'MAIN_SECTION' as const
		}
	},
	// 회원
	{
		path: paths.user.email.send.path,
		element: withSuspense(UserSendEmail),
		handle: {
			title: '이메일 발송하기',
			subTitle: '락앤런 회원 이메일 발송하기',
			permission: 'USERS' as const
		}
	},
	{
		path: paths.user.kakao.send.path,
		element: withSuspense(UserSendKakao),
		handle: {
			title: '카카오톡 발송하기',
			subTitle: '락앤런 회원 카카오톡 발송하기',
			permission: 'USERS' as const
		}
	},
	// 대회
	{
		path: paths.contest.new.path,
		element: withSuspense(ContestCreate),
		handle: {
			title: '새 대회 등록',
			subTitle: '새로운 대회 등록하기',
			permission: 'CONTESTS' as const
		}
	},
	{
		path: paths.contest.edit.path,
		element: withSuspense(ContestUpdate),
		handle: {
			title: '대회 수정',
			subTitle: '등록한 대회 수정하기',
			permission: 'CONTESTS' as const
		}
	},
	{
		path: paths.contest.additionalRecruitment.path,
		element: withSuspense(ContestAdditionalRecruitment),
		handle: {
			title: '추가 모집 설정',
			subTitle: '참가자 미달시 추가 모집 기간 설정',
			permission: 'CONTESTS' as const
		}
	},
	{
		path: paths.contest.email.send.path,
		element: withSuspense(ContestSendEmail),
		handle: {
			title: '이메일 발송하기',
			subTitle: '락앤런 대회 참가자 이메일 발송하기',
			permission: 'CONTESTS' as const
		}
	},
	{
		path: paths.contest.kakao.send.path,
		element: withSuspense(ContestSendKakao),
		handle: {
			title: '카카오톡 발송하기',
			subTitle: '락앤런 대회 참가자 카카오톡 발송하기',
			permission: 'CONTESTS' as const
		}
	},
	// 프로그램
	{
		path: paths.program.new.path,
		element: withSuspense(ProgramCreate),
		handle: {
			title: '새 프로그램 등록',
			subTitle: '새로운 회차 프로그램 등록하기',
			permission: 'PROGRAMS' as const
		}
	},
	{
		path: paths.program.edit.path,
		element: withSuspense(ProgramEdit),
		handle: {
			title: '프로그램 수정',
			subTitle: '등록한 프로그램 수정하기',
			permission: 'PROGRAMS' as const
		}
	},
	// 프로그램 회차
	{
		path: paths.program.session.new.path,
		element: withSuspense(SessionCreate),
		handle: {
			title: '프로그램 회차 등록',
			subTitle: '프로그램 진행 회차 등록하기',
			permission: 'PROGRAMS' as const
		}
	},
	{
		path: paths.program.session.edit.path,
		element: withSuspense(SessionEdit),
		handle: {
			title: '프로그램 회차 수정',
			subTitle: '프로그램 진행 회차 수정하기',
			permission: 'PROGRAMS' as const
		}
	},
	{
		path: paths.program.email.send.path,
		element: withSuspense(ProgramSendEmail),
		handle: {
			title: '이베일 발송하기',
			subTitle: '락앤런 프로그램 참가자 이메일 발송하기',
			permission: 'PROGRAMS' as const
		}
	},
	{
		path: paths.program.kakao.send.path,
		element: withSuspense(ProgramSendKakao),
		handle: {
			title: '카카오톡 발송하기',
			subTitle: '락앤런 프로그램 참가자 카카오톡 발송하기',
			permission: 'PROGRAMS' as const
		}
	},
	// 상품관리
	{
		path: paths.store.new.path,
		element: withSuspense(ProductCreate),
		handle: {
			title: '새 상품 등록',
			subTitle: '스토어 새로운 상품 등록하기',
			permission: 'STORES' as const
		}
	},
	{
		path: paths.store.edit.path,
		element: withSuspense(ProductEdit),
		handle: {
			title: '상품 수정',
			subTitle: '스토어 상품 수정하기',
			permission: 'STORES' as const
		}
	},
	// 운영 관리
	// 공지사항
	{
		path: paths.operation.notice.new.path,
		element: withSuspense(NoticeCreate),
		handle: {
			title: '공지사항 등록',
			subTitle: '락앤런 공지사항 등록하기',
			permission: 'OPERATIONS' as const
		}
	},
	{
		path: paths.operation.notice.edit.path,
		element: withSuspense(NoticeEdit),
		handle: {
			title: '공지사항 수정',
			subTitle: '락앤런 공지사항 수정하기',
			permission: 'OPERATIONS' as const
		}
	},
	// FAQ
	{
		path: paths.operation.faq.new.path,
		element: withSuspense(FaqCreate),
		handle: {
			isGoBack: true,
			title: '자주 묻는 질문 등록',
			subTitle: '운영 > 자주 묻는 질문 > 등록',
			permission: 'OPERATIONS' as const
		}
	},
	{
		path: paths.operation.faq.edit.path,
		element: withSuspense(FaqEdit),
		handle: {
			isGoBack: true,
			title: '자주 묻는 질문 수정',
			subTitle: '운영 > 자주 묻는 질문 > 수정',
			permission: 'OPERATIONS' as const
		}
	},
	// 뉴스
	{
		path: paths.operation.news.new.path,
		element: withSuspense(NewsCreate),
		handle: {
			title: '뉴스 등록',
			subTitle: '락앤런 뉴스 게시글 등록하기',
			permission: 'OPERATIONS' as const
		}
	},
	{
		path: paths.operation.news.edit.path,
		element: withSuspense(NewsEdit),
		handle: {
			title: '뉴스 수정',
			subTitle: '락앤런 뉴스 게시글 수정하기',
			permission: 'OPERATIONS' as const
		}
	},
	// 인스타
	{
		path: paths.operation.instagram.new.path,
		element: withSuspense(InstagramCreate),
		handle: {
			title: '인스타그램 링크 등록',
			subTitle: '락앤런 인스타그램 링크 등록하기',
			permission: 'OPERATIONS' as const
		}
	},
	{
		path: paths.operation.instagram.edit.path,
		element: withSuspense(InstagramEdit),
		handle: {
			title: '인스타그램 링크 수정',
			subTitle: '락앤런 인스타그램 링크 수정하기',
			permission: 'OPERATIONS' as const
		}
	},
	// 유튜브
	{
		path: paths.operation.youtube.new.path,
		element: withSuspense(YoutubeCreate),
		handle: {
			title: '유튜브 링크 등록',
			subTitle: '락앤런 유튜브 링크 등록하기',
			permission: 'OPERATIONS' as const
		}
	},
	{
		path: paths.operation.youtube.edit.path,
		element: withSuspense(YoutubeEdit),
		handle: {
			title: '유튜브 링크 수정',
			subTitle: '락앤런 유튜브 링크 수정하기',
			permission: 'OPERATIONS' as const
		}
	},
	// 개인정보 처리방침
	{
		path: paths.operation.privacyPolicy.new.path,
		element: withSuspense(PrivacyPolicyCreate),
		handle: {
			title: '개인정보 처리방침 등록',
			subTitle: '락앤런 개인정보처 리방침 등록하기',
			permission: 'OPERATIONS' as const
		}
	},
	// 이용약관
	{
		path: paths.operation.termsOfService.new.path,
		element: withSuspense(TermsOfServiceCreate),
		handle: {
			title: '이용약관 등록',
			subTitle: '락앤런 이용약관 등록하기',
			permission: 'OPERATIONS' as const
		}
	},
	// 관리자
	{
		path: paths.system.admin.new.path,
		element: withSuspense(AdminCreate),
		handle: {
			title: '관리자 계정 생성',
			subTitle: '락앤런 관리자 새로운 계정 생성하기',
			permission: 'SETTINGS' as const,
			grades: ADMIN_MANAGE_GRADES // MASTER, AAA 등급만 접근 가능
		}
	},
	{
		path: paths.system.admin.edit.path,
		element: withSuspense(AdminEdit),
		handle: {
			title: '관리자 계정 수정',
			subTitle: '락앤런 관리자 계정 수정하기',
			permission: 'SETTINGS' as const,
			grades: ADMIN_MANAGE_GRADES // MASTER, AAA 등급만 접근 가능
		}
	}
];
